import { Box, Typography } from "@mui/material";
import {
  ItemRenderProps,
  TreeView,
  TreeViewCheckChangeEvent,
  TreeViewExpandChangeEvent,
  handleTreeViewCheckChange,
  processTreeViewItems,
} from "@progress/kendo-react-treeview";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUpdateEffect } from "react-use";
import { clone } from "../../../commons/helpers/ObjectHelper";
import { RefreshButton } from "../../../components/commons/RefreshButton";
import { SavePreferencesButton } from "../../../components/commons/SavePreferencesButton";
import "./customtreeview.css";

interface CustomTreeViewProps {
  data: any[];
  children: any;
  acheteurs?: JSX.Element;
  checkIds: string[];
  useScroll: boolean;
  selectedTab: number;
  invalidateQuery: any;
  totalProducts?: number;
}

export const CustomTreeView = (props: CustomTreeViewProps) => {
  const pageSize = 200;
  const [page, setPage] = useState(1);
  const initialCheckState = useMemo(() => {
    return {
      ids: props.checkIds,
      applyCheckIndeterminate: true,
      idField: "checkId",
    };
  }, [props.checkIds]);
  const [check, setCheck] = useState<any>(initialCheckState);
  const [filteredData, setFilteredData] = useState<any[]>(
    clone(props.data)
  );

  const handleScroll = useCallback(
    (element: Element) => {
      if (filteredData.length > pageSize * page) {
        const elementRect = element.getBoundingClientRect();
        const childRect = element.firstElementChild!.getBoundingClientRect();
        if (Math.abs(elementRect.bottom - childRect.bottom) < 20) {
          setPage(page + 1);
        }
      }
    },
    [filteredData, page]
  );

  useEffect(() => {
    if (props.useScroll) {
      const element = Array.from(
        document.getElementsByClassName(`k-tabstrip-content`)
      )[0];
      const handler = () => handleScroll(element);
      element.addEventListener("wheel", handler, { passive: true });
      element.addEventListener("scroll", handler, { passive: true });

      return () => {
        element.removeEventListener("wheel", handler);
        element.removeEventListener("scroll", handler);
      };
    }
  });

  useEffect(() => {
    const element = Array.from(
      document.getElementsByClassName(`k-tabstrip-content`)
    )[0];
    element.scrollTo(0, 0);
  }, [props.selectedTab]);

  useUpdateEffect(() => {
    setCheck(initialCheckState);
  }, [initialCheckState]);

  useUpdateEffect(() => {
    setFilteredData(clone(props.data));
  }, [props.data]);

  const onCheckChange = useCallback(
    (event: TreeViewCheckChangeEvent) => {
      let levels = event.itemHierarchicalIndex.split("_");
      let copyData = clone(filteredData);
      for (let i = 0; i < levels.length; i++) {
        if (i === levels.length - 1) props.children[i](copyData[levels[i]]);

        copyData = copyData[levels[i]].children;
      }
      setCheck(
        handleTreeViewCheckChange(event, check, filteredData, {}, "children")
      );
    },
    [check, filteredData, props.children]
  );

  const onExpandChange = useCallback(
    async (event: TreeViewExpandChangeEvent) => {
      let copyData = clone(filteredData);
      let levels = event.itemHierarchicalIndex.split("_");
      let lastLevel = levels[levels.length - 1];
      let current = copyData;
      for (let i = 0; i < levels.length; i++) {
        if (i === levels.length - 1) break;

        current = current[levels[i]].children;
      }
      current[lastLevel].expanded = !current[lastLevel].expanded;
      if (current[lastLevel].children?.length === 0)
        current[lastLevel].hasChildren = false;
      setFilteredData(copyData);
    },
    [filteredData]
  );

  const CustomItemRender = useCallback((itemProps: ItemRenderProps) => {
    return (
      <div>
        <span
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {itemProps.item.text}
        </span>
      </div>
    );
  }, []);

  const search = useCallback((items: any[], term: string) => {
    return items.reduce((acc: any[], item: any) => {
      if (item.text.toLowerCase().includes(term.toLowerCase())) acc.push(item);
      else if (item.children && item.children.length > 0) {
        let newItems = search(item.children, term);
        if (newItems && newItems.length > 0)
          acc.push({ ...item, children: newItems });
      }
      return acc;
    }, []);
  }, []);

  const handleSearch = useCallback(
    (event: any) => {
      let data = search(
        props.data.map((d) => {
          let existingData = filteredData.find(
            (f) =>
              (f.four_cd && f.four_cd === d.four_cd) ||
              (f.div_num && f.div_num === d.div_num)
          );
          return existingData ? existingData : d;
        }),
        event.target.value
      );
      setFilteredData(clone(data));
    },
    [filteredData, props.data, search]
  );

  return (
    <div className="treeview-wrapper">
      <Box>
        <input
          className="k-textbox treeview-search-input" 
          onChange={handleSearch}
          placeholder="Rechercher"
        />
        <RefreshButton invalidateQueries={props.invalidateQuery} />
        <SavePreferencesButton />
      </Box>
      {props.acheteurs}
      <Typography sx={{fontWeight:"bold", paddingInlineStart:"24px"}}>
        {props.totalProducts ? `${props.totalProducts} résultat(s)` : "Aucun résultat"}
      </Typography>
      <TreeView
        data={processTreeViewItems(filteredData.slice(0, pageSize * page), {
          check: check,
          childrenField: "children",
        })}
        expandIcons={true}
        checkboxes={true}
        animate={false}
        aria-multiselectable={true}
        size={"small"}
        onExpandChange={onExpandChange}
        onCheckChange={onCheckChange}
        onItemClick={onCheckChange}
        item={CustomItemRender}
        childrenField="children"
      />
    </div>
  );
}
